import { useState } from 'react'
import { Power, PowerOff } from 'lucide-react'

import { IrrigationRunDialog } from './irrigation-run-dialog'
import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useStopIrrigationPump } from '@/hooks/use-irrigation-devices-query'
import { useNow } from '@/hooks/use-now'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/**
 * Nút bật/tắt bơm của MỘT bộ tưới. Cả hai nút chỉ mở hộp thoại; lệnh THẬT chỉ
 * gửi khi bấm nút xác nhận trong hộp thoại.
 *
 * Bật luôn kèm thời lượng (hộp thoại tưới ngay / hẹn giờ) nên bơm luôn có giờ
 * tắt. Biết trạng thái thì chỉ hiện nút đổi sang trạng thái kia; chưa rõ thì
 * hiện cả hai, vì người vận hành vẫn phải tắt được bơm để về trạng thái an toàn.
 */
export function IrrigationPumpAction({
    device,
    showCamera = true,
    onRunDialogChange,
    buttonClassName,
    fillRow = false,
    countdownInButton = false,
}: {
    device: IrrigationDevice
    /** Truyền xuống hộp thoại Bật (xem IrrigationRunDialog.showCamera). */
    showCamera?: boolean
    /**
     * Báo hộp thoại Bật mở/đóng — thẻ trạm tạm ngắt luồng của mình trong lúc
     * hộp thoại mở luồng riêng, để mỗi camera chỉ một kết nối.
     */
    onRunDialogChange?: (open: boolean) => void
    /** Lớp thêm cho nút Bật/Tắt, vd nút cao hơn trên điện thoại ở thẻ trạm. */
    buttonClassName?: string
    /** Nút Bật/Tắt chia đều cả hàng (thẻ trạm). */
    fillRow?: boolean
    /**
     * Đếm ngược 1 phút nghỉ hiện NGAY TRONG nút Bật thay cho dòng chữ bên dưới —
     * thẻ trạm cần hàng nút cao bằng nhau để các thẻ cạnh nhau thẳng hàng.
     */
    countdownInButton?: boolean
}) {
    const [runOpen, setRunOpenState] = useState(false)
    const setRunOpen = (open: boolean) => {
        setRunOpenState(open)
        onRunDialogChange?.(open)
    }
    const [stopOpen, setStopOpen] = useState(false)
    const stop = useStopIrrigationPump()

    const known = device.reading_state === 'ok' && device.pump_on !== null
    const showOn = !known || device.pump_on === false
    const showOff = !known || device.pump_on === true

    const confirmStop = () => {
        // Đóng hộp thoại cả khi lỗi: toast đã nói rõ lý do, giữ hộp thoại mở
        // dễ khiến người dùng bấm lặp lệnh.
        stop.mutate(device, { onSettled: () => setStopOpen(false) })
    }

    return (
        <>
            <div
                className={twJoin(
                    'flex flex-wrap gap-2',
                    fillRow && 'flex-nowrap *:flex-1',
                )}
            >
                {showOn && (
                    <TurnOnButton
                        availableAt={device.pump_on_available_at}
                        onClick={() => setRunOpen(true)}
                        className={buttonClassName}
                        stretch={fillRow}
                        countdownInButton={countdownInButton}
                    />
                )}
                {showOff && (
                    <Button
                        size="sm"
                        variant="outline"
                        className={buttonClassName}
                        onClick={() => setStopOpen(true)}
                        disabled={stop.isPending}
                    >
                        <PowerOff className="size-4" />
                        {m.irrigation_pump_turn_off()}
                    </Button>
                )}
            </div>

            <IrrigationRunDialog
                device={device}
                open={runOpen}
                onOpenChange={setRunOpen}
                showCamera={showCamera}
            />

            <Dialog
                open={stopOpen}
                onOpenChange={(open) => {
                    if (!open && !stop.isPending) setStopOpen(false)
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.irrigation_pump_confirm_off_title({
                                name: device.name,
                            })}
                        </DialogTitle>
                        <DialogDescription>
                            {m.irrigation_pump_confirm_off_desc()}
                        </DialogDescription>
                    </DialogHeader>
                    <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                        <li>{m.irrigation_pump_confirm_manual_mode()}</li>
                        <li>{m.irrigation_pump_confirm_state_delay()}</li>
                    </ol>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setStopOpen(false)}
                            disabled={stop.isPending}
                        >
                            {m.irrigation_pump_confirm_cancel()}
                        </Button>
                        <Button onClick={confirmStop} disabled={stop.isPending}>
                            {stop.isPending
                                ? m.irrigation_pump_sending()
                                : m.irrigation_pump_confirm_off_submit()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

/** "0:42" — phút:giây còn lại. */
function formatLeft(seconds: number): string {
    const mm = Math.floor(seconds / 60)
    const ss = String(seconds % 60).padStart(2, '0')
    return `${mm}:${ss}`
}

/**
 * Nút Bật. Trong 1 phút nghỉ sau lệnh gần nhất, nút bị khoá và ghi rõ còn
 * bao lâu: bật/tắt liên tục có thể cháy máy bơm. Server vẫn là nơi chặn
 * thật (IRRIGATION_PUMP_COOLDOWN); đếm ngược ở đây chỉ để người dùng biết.
 */
function TurnOnButton({
    availableAt,
    onClick,
    className,
    stretch = false,
    countdownInButton = false,
}: {
    availableAt: string | null
    onClick: () => void
    className?: string
    stretch?: boolean
    countdownInButton?: boolean
}) {
    const now = useNow(1_000)
    const left = availableAt
        ? Math.max(
              0,
              Math.ceil((Date.parse(availableAt) - now.getTime()) / 1000),
          )
        : 0

    return (
        <div
            className={twJoin(
                'flex flex-col gap-1',
                stretch ? 'items-stretch' : 'items-start',
            )}
        >
            <Button
                size="sm"
                onClick={onClick}
                disabled={left > 0}
                className={className}
            >
                <Power className="size-4" />
                <span className="tabular-nums">
                    {countdownInButton && left > 0
                        ? m.irrigation_pump_cooldown_hint({
                              time: formatLeft(left),
                          })
                        : m.irrigation_pump_turn_on()}
                </span>
            </Button>
            {left > 0 && !countdownInButton && (
                <span className="text-xs tabular-nums text-muted-foreground">
                    {m.irrigation_pump_cooldown_hint({
                        time: formatLeft(left),
                    })}
                </span>
            )}
        </div>
    )
}
